import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Trash, ArrowLeft, Printer, Receipt, Ticket, PencilSimple, CurrencyInr, ArrowsLeftRight, ArrowDown, ArrowUp, LockKey, LinkSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { CustomerCombo, ServiceCombo } from '@/components/InvoiceCombos';
import { AddCustomerDialog, AddServiceDialog } from '@/components/InvoiceDialogs';
import { useCan } from '@/lib/permissions';
import PaymentReceiptDialog from '@/components/PaymentReceiptDialog';
import CustomerLedgerDialog from '@/components/CustomerLedgerDialog';
import QuickFinanceDialog from '@/components/QuickFinanceDialog';
import CustomerCredentialsDialog from '@/components/CustomerCredentialsDialog';
import EditCustomerDialog from '@/components/EditCustomerDialog';
import RecurringDatesDialog from '@/components/RecurringDatesDialog';
import { BillHistoryCard } from '@/components/BillHistoryCard';
import { printInvoice as sharedPrintInvoice } from '@/lib/printInvoice';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
const noSpinStyle = { MozAppearance: 'textfield' };

/**
 * Volume-pricing resolver. When a service has `multi_price=true` + a
 * `price_tiers` array, find the tier whose [min_qty..max_qty] range covers
 * the current quantity and return that tier's per-unit price.
 * For `tier_mode='price'` (named variants), the caller passes the picked
 * variant label to `variantLabel`; we look it up directly instead of
 * using qty. Falls back to the service's base `service_charge` when
 * nothing matches (qty below the lowest tier, or no tiers configured).
 */
function _priceForQty(svc, qty, variantLabel) {
  if (!svc || !svc.multi_price || !Array.isArray(svc.price_tiers) || svc.price_tiers.length === 0) {
    return Number(svc?.service_charge) || 0;
  }
  if (svc.tier_mode === 'price') {
    if (variantLabel) {
      const t = svc.price_tiers.find(x => String(x.label) === String(variantLabel));
      if (t) return Number(t.price) || 0;
    }
    // Default to cheapest variant when no label is set yet.
    return Number(svc.price_tiers[0]?.price) || 0;
  }
  const n = Math.max(1, parseInt(qty) || 1);
  for (const t of svc.price_tiers) {
    const mn = Number(t.min_qty) || 1;
    const mx = t.max_qty == null || t.max_qty === '' ? Infinity : Number(t.max_qty);
    if (n >= mn && n <= mx) return Number(t.price) || 0;
  }
  return Number(svc.service_charge) || 0;
}

export default function CreateInvoicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const tokenParam = searchParams.get('token');
  // Local mirror of the draft id — starts from the URL but is also updated
  // after a fresh Save-as-Draft so subsequent saves on the same page
  // update the existing draft instead of creating duplicates.
  const [currentDraftId, setCurrentDraftId] = useState(draftId || '');
  const [dateUnlocked, setDateUnlocked] = useState(false);
  const [draftPreservedNo, setDraftPreservedNo] = useState('');
  useEffect(() => { setCurrentDraftId(draftId || ''); }, [draftId]);
  // Ref for the Customer combobox trigger so we can autofocus it on mount
  // (Tabstop 1 = Customer, Tabstop 2 = first Item / service select).
  const customerTriggerRef = useRef(null);
  // When navigated to from the Customer Ledger dialog, two extra params control
  // pre-selection + post-save navigation:
  //   ?customer=<id>           — pre-select this customer in the customer combo
  //   ?return_to_ledger=1      — after a successful save, return to /customers
  //                              with ?openLedger=<customer_id> so the dialog
  //                              re-opens automatically.
  const customerParam = searchParams.get('customer');
  const returnToLedger = searchParams.get('return_to_ledger') === '1';
  const ledgerReturnTarget = returnToLedger && customerParam ? `/customers?openLedger=${encodeURIComponent(customerParam)}` : '/invoices';
  const goBack = () => navigate(ledgerReturnTarget);
  const { user } = useAuth();
  const canAssign = useCan('work_assign', 'view');
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [customerId, setCustomerId] = useState('');
  // Staff directory for the per-item Assignee dropdown. Loaded once on mount.
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [items, setItems] = useState([{
    service_id: '', service_name: '', quantity: 1, service_charge: 0, govt_fee: 0,
    govt_fee_mode: '', work_status: 'submitted', total: 0, remark: '', followup: false,
    assigned_to: '',
  }]);
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentType, setPaymentType] = useState('full');
  const [amountPaidStr, setAmountPaidStr] = useState('');
  const [excessHandling, setExcessHandling] = useState('change'); // 'change' | 'advance'
  const [advanceUsed, setAdvanceUsed] = useState(0);
  const [discountStr, setDiscountStr] = useState('');
  const [notes, setNotes] = useState('');
  // Reason captured when the staff member saves the bill as a draft. It's
  // required so the next person picking up the task knows why it was parked.
  const [draftReason, setDraftReason] = useState('');
  const [showDraftReasonPrompt, setShowDraftReasonPrompt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Once the invoice is saved (as Credit), we hold the server response here so
  // the Pay/Print buttons and the post-save banner can reference it. Flips the
  // bottom action bar from [Token/Draft/Create/Cancel] → [Print/Pay/Cancel].
  const [savedInvoice, setSavedInvoice] = useState(null);
  const [cleanSig, setCleanSig] = useState('');
  const itemsSig = JSON.stringify(items.filter(i => i.service_id));
  const isDirty = !savedInvoice && itemsSig !== '[]' && itemsSig !== cleanSig;
  const confirmLeave = useCallback(() => !isDirty || window.confirm('This bill has unsaved items. Leave without saving?'), [isDirty]);
  useEffect(() => {
    if (!isDirty) return;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    const onClick = (e) => {
      const a = e.target.closest?.('a[href]');
      if (!a || a.target === '_blank' || a.getAttribute('href').startsWith('#')) return;
      if (!window.confirm('This bill has unsaved items. Leave without saving?')) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    return () => { window.removeEventListener('beforeunload', onUnload); document.removeEventListener('click', onClick, true); };
  }, [isDirty]);
  // After saving, the form locks down — fields are read-only and the row
  // actions hide. Clicking "Edit Invoice" flips this back on so the user
  // can append items, tweak Discount / Remarks, then click "Update Invoice"
  // to PUT the changes.
  const [editingSavedInvoice, setEditingSavedInvoice] = useState(false);
  // null | 'transfer' | 'receipt' | 'income' | 'expense' — drives the
  // fullscreen Quick Finance popup so the user can record an unrelated
  // entry without leaving the invoice draft.
  const [quickFinanceMode, setQuickFinanceMode] = useState(null);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [addServiceForRow, setAddServiceForRow] = useState(-1);
  const [addServiceName, setAddServiceName] = useState('');
  const [custForm, setCustForm] = useState({ name: '', phone: '', address: '', notes: '' });
  const [focusNewRow, setFocusNewRow] = useState(false);
  const [finAccounts, setFinAccounts] = useState([]);
  const [custLedger, setCustLedger] = useState(null);
  const [showPayDue, setShowPayDue] = useState(null);
  const [duePayAmount, setDuePayAmount] = useState('');
  const [duePayMode, setDuePayMode] = useState('');
  const [duePayDate, setDuePayDate] = useState(new Date().toISOString().split('T')[0]);
  const [showLedgerPopup, setShowLedgerPopup] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  // When a recurring service is picked, we open the dates popup with this
  // row's index so the operator can confirm/back-date the From date. Null
  // = dialog hidden.
  const [recurringDialog, setRecurringDialog] = useState(null);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState('');
  const [manualInvoiceNo, setManualInvoiceNo] = useState(false);
  const useAutoInvoiceNo = () => {
    setManualInvoiceNo(false);
    api.get('/invoices/next-number').then(r => setPreviewInvoiceNumber(r.data.invoice_number)).catch(() => {});
  };
  // Customer advance history (entries used to render the Advance Received block
  // beneath the Due row when the customer has unused advance balance).
  const [advHistory, setAdvHistory] = useState({ balance: 0, entries: [] });
  const [applyingAdv, setApplyingAdv] = useState(false);
  // Per-receipt unadjusted credit (overpayments + standalone advances) that
  // the customer still has against ANY bill. Surfaced in a panel between the
  // recorded-receipts list and the Due row after the invoice is saved, so
  // the staff can adjust them against the current bill with one click.
  const [unadjusted, setUnadjusted] = useState({ receipts: [], unadjusted_total: 0 });
  const [applyingReceiptId, setApplyingReceiptId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  // When the customer actually paid — defaults to the invoice date but can be edited
  // (e.g. UPI received next morning). Used to stamp finance txns so reports reflect
  // when the money actually entered the account.
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  // Today's date — used as the `max` attr on every date input so users can only
  // back-date (never future-date) an invoice or its payment.
  const todayIso = new Date().toISOString().split('T')[0];
  // Token state — either creating a new one (saving as token) or editing one already saved.
  const [tokenId, setTokenId] = useState('');            // id of the token being edited
  const [tokenNumber, setTokenNumber] = useState('');    // "T007"
  // Optional "Applicant" name — the person the service is actually for. Auto-
  // syncs to the selected customer's name unless the operator has typed an
  // override (see CustomerCombo `onSelect` handler above). Trimmed value is
  // sent as `applicant_name` on invoice / draft / token payloads so downstream
  // pages can render it under the customer name when it differs.
  const [applicantName, setApplicantName] = useState('');

  const fetchData = () => {
    api.get('/customers').then(r => setCustomers(r.data)).catch(console.error);
    api.get('/services', { params: { include_archived: false } }).then(r => setServices(r.data)).catch(console.error);
    api.get('/finance/accounts').then(r => {
      setFinAccounts(r.data);
      // Default payment mode → first CASH account; fall back to first bank if no cash exists.
      if (!paymentMode) {
        const cash = r.data.find(a => a.type === 'cash');
        const fallback = r.data.find(a => a.type === 'bank');
        const pick = cash || fallback;
        if (pick) setPaymentMode(pick.id);
      }
    }).catch(console.error);
    api.get('/invoices/next-number').then(r => setPreviewInvoiceNumber(r.data.invoice_number)).catch(() => {});
    api.get('/work/staff').then(r => setStaffDirectory(r.data || [])).catch(() => {});
  };
  useEffect(() => { fetchData(); }, []);

  // Load draft data if editing a draft
  useEffect(() => {
    if (draftId) {
      api.get(`/drafts/${draftId}`).then(r => {
        const d = r.data;
        setCustomerId(d.customer_id || '');
        setApplicantName(d.applicant_name || d.customer_name || '');
        if (d.items?.length > 0) setCleanSig(JSON.stringify(d.items.map(it => ({ ...it, followup: it.followup || false })).filter(i => i.service_id)));
        setItems(d.items?.length > 0 ? d.items.map(it => ({ ...it, followup: it.followup || false })) : [{ service_id: '', service_name: '', quantity: 1, service_charge: 0, govt_fee: 0, govt_fee_mode: '', work_status: 'submitted', total: 0, remark: '', followup: false }]);
        setNotes(d.notes || '');
        setDraftReason(d.reason || '');
        // If the draft was created from an existing invoice (Make Draft demote)
        // OR the user typed in a custom serial earlier, restore that number so
        // the next save re-issues the SAME bill number instead of grabbing a
        // fresh INV-N from the auto-counter.
        const preserved = (d.invoice_number || '').trim();
        if (preserved) { setPreviewInvoiceNumber(preserved); setManualInvoiceNo(true); setDraftPreservedNo(preserved); }
      }).catch(() => toast.error('Draft not found'));
    }
  }, [draftId]);

  // Duplicate flow — `?duplicate_from=invoice:<id>` or `?duplicate_from=draft:<id>`.
  // Pulls the source row, then prefills the customer + items + notes into a
  // *fresh* form. We deliberately skip the invoice/voucher number, the
  // receipts/payments and the work-status / followup flags so the duplicate
  // is a clean new bill the staff can edit and save as their own draft or
  // completed invoice. `currentDraftId` stays empty so Save-as-Draft will
  // POST a new draft rather than overwrite the source.
  useEffect(() => {
    const dup = searchParams.get('duplicate_from');
    if (!dup) return;
    const [kind, id] = dup.split(':');
    if (!kind || !id) return;
    const endpoint = kind === 'invoice' ? `/invoices/${id}` : `/drafts/${id}`;
    api.get(endpoint).then(r => {
      const src = r.data;
      setCustomerId(src.customer_id || '');
      // When duplicating, carry the applicant name over too — it's part of
      // the "who is this service for" context the operator likely wants to
      // keep on the new bill.
      setApplicantName(src.applicant_name || src.customer_name || '');
      const srcItems = src.items?.length > 0
        ? src.items.map(it => ({
            service_id: it.service_id || '',
            service_name: it.service_name || '',
            quantity: it.quantity || 1,
            service_charge: it.service_charge || 0,
            govt_fee: it.govt_fee || 0,
            govt_fee_mode: it.govt_fee_mode || '',
            // Reset work-flow state — the duplicate is a brand-new request.
            work_status: 'submitted',
            total: it.total || ((it.service_charge || 0) + (it.govt_fee || 0)) * (it.quantity || 1),
            remark: it.remark || '',
            followup: it.followup || false,
            assigned_to: '',
          }))
        : [{ service_id: '', service_name: '', quantity: 1, service_charge: 0, govt_fee: 0, govt_fee_mode: '', work_status: 'submitted', total: 0, remark: '', followup: false, assigned_to: '' }];
      setItems(srcItems);
      setNotes(src.notes || '');
      // Source label for the toast — invoice_number when duplicating a
      // saved invoice, fallback to "Draft" when duplicating a draft.
      const label = kind === 'invoice' ? (src.invoice_number || 'invoice') : 'draft';
      toast.success(`Duplicated from ${label} — review and save as new`);
    }).catch(() => toast.error('Source not found for duplicate'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the token into the form when ?token=<id> is provided.
  useEffect(() => {
    if (!tokenParam) return;
    api.get(`/tokens/${tokenParam}`).then(r => {
      const t = r.data;
      setTokenId(t.id);
      setTokenNumber(t.token_number);
      setCustomerId(t.customer_id || '');
      setApplicantName(t.applicant_name || t.customer_name || '');
      setItems(t.items?.length > 0
        ? t.items.map(it => ({ ...it, followup: it.followup || false }))
        : [{ service_id: '', service_name: '', quantity: 1, service_charge: 0, govt_fee: 0, govt_fee_mode: '', work_status: 'submitted', total: 0, remark: '', followup: false }]);
      setNotes(t.notes || '');
      toast.success(`Editing ${t.token_number}`);
    }).catch(() => toast.error('Token not found'));
  }, [tokenParam]);

  // When linked from the Customer Ledger ("Create Invoice" quick action), the
  // ?customer=<id> param pre-selects the customer once the list has loaded.
  useEffect(() => {
    if (customerParam && customers.length > 0 && !customerId) {
      setCustomerId(customerParam);
    }
  }, [customerParam, customers, customerId]);

  // ?service=<id> param (used by the Recurring → Renew flow) pre-selects
  // the service in row 0 once both services & accounts are loaded. We
  // intentionally wait for `finAccounts` so the row's default fee mode
  // resolves correctly via `selectServiceForRow`.
  const servicePrefilledRef = useRef(false);
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    if (!serviceParam) return;
    if (servicePrefilledRef.current) return;
    if (services.length === 0 || finAccounts.length === 0) return;
    const svc = services.find(s => s.id === serviceParam);
    if (!svc) return;
    servicePrefilledRef.current = true;
    selectServiceForRow(0, svc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, finAccounts, searchParams]);

  // Calculate used govt_fee balance per account across all rows
  const getAvailableBalance = (accountId, currentRowIdx) => {
    const account = finAccounts.find(a => a.id === accountId);
    if (!account) return 0;
    let usedByOthers = 0;
    items.forEach((item, i) => {
      if (i !== currentRowIdx && item.govt_fee_mode === accountId && item.govt_fee > 0) {
        usedByOthers += item.govt_fee * item.quantity;
      }
    });
    return account.balance - usedByOthers;
  };

  const selectedCustomer = customers.find(c => c.id === customerId);

  // Auto-fill Applicant with the selected customer's name whenever the field
  // is blank. Covers URL prefill (?customer=<id>) and any code path that
  // sets customerId without also setting applicantName (e.g. add-new customer
  // popup). Manual overrides are preserved because we only fill on empty.
  useEffect(() => {
    if (selectedCustomer && !applicantName) {
      setApplicantName(selectedCustomer.name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id]);

  // Fetch customer ledger summary when customer changes
  useEffect(() => {
    setAdvanceUsed(0); // reset advance application on customer change
    if (customerId) {
      api.get(`/customers/${customerId}/ledger`).then(r => {
        const entries = r.data;
        const totalBilled = entries.reduce((s, e) => s + (e.debit || 0), 0);
        const totalReceived = entries.reduce((s, e) => s + (e.credit || 0), 0);
        const pendingDue = totalBilled - totalReceived;
        setCustLedger({ entries, totalBilled, totalReceived, pendingDue });
      }).catch(() => setCustLedger(null));
      api.get(`/customers/${customerId}/advance-history`).then(r => {
        setAdvHistory({ balance: r.data?.advance_balance || 0, entries: r.data?.entries || [] });
      }).catch(() => setAdvHistory({ balance: 0, entries: [] }));
    } else {
      setCustLedger(null);
      setAdvHistory({ balance: 0, entries: [] });
    }
  }, [customerId, savedInvoice]);

  const handleApplyAdvanceToSaved = async () => {
    if (!savedInvoice) return;
    const due = savedInvoice.balance_due ?? 0;
    if (due <= 0) { toast.error('No due to apply against'); return; }
    const amt = Math.min(advHistory.balance, due);
    if (amt <= 0) { toast.error('No advance available'); return; }
    setApplyingAdv(true);
    try {
      const r = await api.post(`/invoices/${savedInvoice.id}/apply-advance`, { amount: amt });
      toast.success(`Advance ${fmt(amt)} applied — receipt ${r.data.receipt_number}`);
      // Once any payment is recorded, return to the invoice list (matches the
      // standard CSC counter flow: bill → collect → next customer).
      goBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply advance');
    } finally { setApplyingAdv(false); }
  };

  // Fetch the unadjusted-receipts list for the saved invoice's customer
  // whenever the invoice or its balance changes.
  useEffect(() => {
    if (!savedInvoice?.customer_id) { setUnadjusted({ receipts: [], unadjusted_total: 0 }); return; }
    api.get(`/customers/${savedInvoice.customer_id}/unadjusted-receipts`)
      .then(r => setUnadjusted({
        receipts: (r.data?.receipts || []).filter(x => (x.unapplied_amount || 0) > 0.01),
        unadjusted_total: r.data?.unadjusted_total || 0,
      }))
      .catch(() => setUnadjusted({ receipts: [], unadjusted_total: 0 }));
  }, [savedInvoice?.id, savedInvoice?.balance_due]);

  // Apply ONE specific unadjusted receipt against the current saved invoice.
  // The applied amount is clamped to the remaining bill due so any extra
  // stays in the receipt pool and shows up again on the next bill of the
  // same customer (per product spec).
  const handleApplyUnadjustedReceipt = async (rcp) => {
    if (!savedInvoice) return;
    const due = savedInvoice.balance_due ?? 0;
    if (due <= 0) { toast.error('Bill is already settled'); return; }
    const amt = Math.min(rcp.unapplied_amount || 0, due);
    if (amt <= 0) return;
    setApplyingReceiptId(rcp.txn_id);
    try {
      await api.post(`/invoices/${savedInvoice.id}/apply-advance`, {
        amount: amt,
        source_txn_id: rcp.txn_id,
      });
      toast.success(`${fmt(amt)} from ${rcp.voucher_number || 'receipt'} applied to ${savedInvoice.invoice_number}`);
      // Refresh invoice + unadjusted list AND the customer's advance balance
      // so the "Advance Held" chip on the summary card stays in sync.
      const cid = savedInvoice.customer_id;
      const [inv, ur, adv, custs] = await Promise.all([
        api.get(`/invoices/${savedInvoice.id}`),
        api.get(`/customers/${cid}/unadjusted-receipts`),
        api.get(`/customers/${cid}/advance-history`),
        api.get('/customers'),
      ]);
      setSavedInvoice(inv.data);
      setUnadjusted({
        receipts: (ur.data?.receipts || []).filter(x => (x.unapplied_amount || 0) > 0.01),
        unadjusted_total: ur.data?.unadjusted_total || 0,
      });
      setAdvHistory({ balance: adv.data?.advance_balance || 0, entries: adv.data?.entries || [] });
      setCustomers(custs.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply receipt');
    } finally { setApplyingReceiptId(''); }
  };

  // Delete a receipt from the saved invoice. After delete we refresh the
  // invoice, the unadjusted-receipts pool AND the customer's advance balance
  // so the "Advance Held" chip on the customer summary card mirrors reality.
  const handleDeleteSavedReceipt = async (receiptNumber) => {
    if (!savedInvoice || !receiptNumber) return;
    if (!window.confirm(`Delete receipt ${receiptNumber}? Linked finance entries will be reversed and the customer's advance / outstanding balance will be restored.`)) return;
    try {
      await api.delete(`/invoices/${savedInvoice.id}/receipts/${receiptNumber}`);
      toast.success(`Receipt ${receiptNumber} deleted`);
      const inv = await api.get(`/invoices/${savedInvoice.id}`);
      setSavedInvoice(inv.data);
      // Refresh advance balance (customer summary card pulls from this) +
      // unadjusted-receipts pool used by the panel below the receipts list.
      const cid = savedInvoice.customer_id;
      const [adv, ur, custs] = await Promise.all([
        api.get(`/customers/${cid}/advance-history`),
        api.get(`/customers/${cid}/unadjusted-receipts`),
        api.get('/customers'),
      ]);
      setAdvHistory({ balance: adv.data?.advance_balance || 0, entries: adv.data?.entries || [] });
      setUnadjusted({
        receipts: (ur.data?.receipts || []).filter(x => (x.unapplied_amount || 0) > 0.01),
        unadjusted_total: ur.data?.unadjusted_total || 0,
      });
      setCustomers(custs.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  // Delete the just-saved invoice (only available when no receipts are
  // attached — UI also gates the button so the user never reaches the API
  // with payments still on the bill).
  const handleDeleteSavedInvoice = async () => {
    if (!savedInvoice) return;
    const hasReceipts = (savedInvoice.payment_history || []).length > 0;
    if (hasReceipts) {
      toast.error('Cannot delete invoice with receipts. Delete receipts first.');
      return;
    }
    if (!window.confirm(`Delete invoice ${savedInvoice.invoice_number}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${savedInvoice.id}`);
      toast.success(`Invoice ${savedInvoice.invoice_number} deleted`);
      goBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const addEmptyItem = () => {
    const defaultFeeMode = finAccounts.length > 0 ? finAccounts[0].id : '';
    // Fresh row starts with EMPTY work_status — once a service is picked,
    // `selectServiceForRow` sets it to 'completed' (if no followup) or
    // leaves it blank (forcing the operator to pick a stage manually).
    // Functional update — guarantees we append to the LATEST items array even
    // when triggered from a stale keyboard-handler closure (Alt+I). Prevents
    // previously-typed rows from being silently reverted to their last
    // re-render snapshot.
    setItems(prev => [...prev, {
      service_id: '', service_name: '', quantity: 1, service_charge: 0, govt_fee: 0,
      govt_fee_mode: defaultFeeMode, work_status: '', total: 0, remark: '', followup: false,
      assigned_to: '',
    }]);
    setFocusNewRow(true);
  };

  // Gate for the "Add Item" action — staff cannot pile a new empty row on
  // top of an existing row that still has a blank work_status. Forces them
  // to either pick the stage (when followup=true) or finish selecting a
  // service (which auto-defaults the stage when followup=false).
  const canAddItem = () => items.every(it => !!it.work_status);

  useEffect(() => {
    if (focusNewRow) {
      // Click the service combo of the newly appended row to open the dropdown
      const lastIdx = items.length - 1;
      const el = document.querySelector(`[data-testid="service-select-${lastIdx}"]`);
      if (el) el.click();
      setFocusNewRow(false);
    }
  }, [focusNewRow, items.length]);

  // After the user picks a customer, jump focus straight into the first
  // service (item) row's search input. Radix Popover ignores a plain
  // `.click()` — it toggles on `pointerdown` — so we fire a matching
  // pointer event, then poll a couple of frames until the CommandInput
  // mounts and take explicit focus. This guarantees the caret lands in
  // the item text box and NEVER on the Customer Summary or Invoice Meta
  // cards.
  useEffect(() => {
    if (!customerId) return;
    if (savedInvoice && !editingSavedInvoice) return;
    const firstService = items?.[0]?.service_id;
    if (firstService) return;

    const openServiceCombo = () => {
      const trigger = document.querySelector('[data-testid="service-select-0"]');
      if (!trigger) return false;
      // Radix combobox uses pointer events, not synthetic clicks.
      const opts = { bubbles: true, cancelable: true, pointerType: 'mouse', button: 0 };
      trigger.dispatchEvent(new PointerEvent('pointerdown', opts));
      trigger.dispatchEvent(new PointerEvent('pointerup', opts));
      trigger.dispatchEvent(new MouseEvent('click', opts));
      return true;
    };
    const focusServiceInput = () => {
      const input = document.querySelector('[data-testid="service-select-0-input"]');
      if (input) { input.focus(); return true; }
      return false;
    };

    const t1 = setTimeout(() => {
      openServiceCombo();
      // Poll a few frames for the input to appear and grab focus.
      let tries = 0;
      const poll = () => {
        if (focusServiceInput()) return;
        if (tries++ < 20) requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    }, 60);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Autofocus the Customer combo on mount and open the dropdown so the
  // user can start typing the customer name / mobile immediately — no
  // intermediate click required. Wait one tick so the Popover trigger is
  // mounted. Only runs when the form is editable AND no customer has
  // been pre-selected yet (e.g. via ?customer= OR loaded from an
  // existing invoice / draft / token on the edit page).
  // We use a ref so this only fires once per page load — once the form
  // hydrates with an existing customer, the dropdown stays closed.
  const customerAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (customerAutoOpenedRef.current) return;
    // Mark immediately so a later customerId-change effect doesn't
    // re-trigger the click.
    if (customerId || customerParam) {
      customerAutoOpenedRef.current = true;
      return;
    }
    const t = setTimeout(() => {
      if (customerAutoOpenedRef.current) return;
      if (customerTriggerRef.current && !customerId && !customerParam) {
        customerAutoOpenedRef.current = true;
        customerTriggerRef.current.click();
      }
    }, 250);     // Slight delay to let any URL-driven hydration land first.
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, customerParam]);

  // Global keyboard shortcut — Alt+I appends a new item row. Mirrors the
  // existing "Add Item" button so power users can keep their hands on the
  // keyboard while billing.
  // Alt+N — once an invoice has been saved, jumps to a fresh blank invoice
  // for back-to-back walk-ins. Mirrors the on-screen "Create Invoice"
  // button that only appears after `savedInvoice` is set.
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when the savedInvoice form is locked OR when the user is
      // typing inside a form input that already handles its own Alt-keys.
      if (e.altKey && (e.key === 'i' || e.key === 'I')) {
        if (savedInvoice && !editingSavedInvoice) return;
        e.preventDefault();
        if (!canAddItem()) {
          toast.error('Pick a work status for the current item before adding a new one');
          return;
        }
        addEmptyItem();
      }
      if (e.altKey && (e.key === 'n' || e.key === 'N')) {
        if (!savedInvoice) return;
        e.preventDefault();
        window.location.assign('/invoices/create');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, savedInvoice, editingSavedInvoice, finAccounts]);

  const updateItem = (idx, field, value) => {
    setItems(items.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: value };
      if (field === 'service_id') {
        const svc = services.find(s => s.id === value);
        if (svc) { updated.service_name = svc.name; updated.service_charge = svc.service_charge; updated.govt_fee = svc.govt_fee; }
      }
      // Followup toggle drives work_status defaulting: turning follow-up
      // OFF means the bill is handed over straight away → mark Submitted.
      // Turning it ON resets work_status so the operator MUST pick the
      // correct in-flight stage before they're allowed to add another item.
      if (field === 'followup') {
        updated.work_status = value ? '' : 'completed';
        if (value) {
          // Auto-open the Work Status dropdown so staff are immediately
          // nudged to pick the in-flight stage. Defer to next tick so the
          // re-render with the new `work_status=''` has landed first.
          setTimeout(() => {
            document.querySelector(`[data-testid="work-status-${idx}"]`)?.click();
          }, 60);
        }
      }
      const qty = field === 'quantity' ? (parseInt(value) || 1) : updated.quantity;
      // Volume pricing — when qty changes on a volume-based multi-price
      // service, snap the per-unit charge to whichever tier now applies.
      // Skip for price-based tiers (they aren't qty-driven) and for direct
      // manual edits to service_charge.
      let sc = field === 'service_charge' ? (parseFloat(value) || 0) : updated.service_charge;
      if (field === 'quantity') {
        const svc = services.find(s => s.id === updated.service_id);
        if (svc && svc.multi_price && svc.tier_mode !== 'price') sc = _priceForQty(svc, qty);
      }
      const gf = field === 'govt_fee' ? (parseFloat(value) || 0) : updated.govt_fee;
      updated.quantity = qty; updated.service_charge = sc; updated.govt_fee = gf;
      updated.total = qty * (sc + gf);

      // If govt fee becomes zero, clear the fee mode
      if (gf === 0) {
        updated.govt_fee_mode = '';
      } else if (field === 'govt_fee') {
        // When govt fee is being set/changed to a non-zero value, auto-apply the service's default fee mode
        const svc = services.find(s => s.id === updated.service_id);
        if (svc?.default_fee_mode) {
          updated.govt_fee_mode = svc.default_fee_mode;
        }
      }

      // Validate fee mode balance whenever fee/qty/mode changes
      if ((field === 'govt_fee' || field === 'quantity' || field === 'govt_fee_mode') && updated.govt_fee_mode && updated.govt_fee > 0) {
        const account = finAccounts.find(a => a.id === updated.govt_fee_mode);
        if (account) {
          let usedByOthers = 0;
          items.forEach((other, oi) => {
            if (oi !== idx && other.govt_fee_mode === updated.govt_fee_mode && other.govt_fee > 0) {
              usedByOthers += other.govt_fee * other.quantity;
            }
          });
          const available = account.balance - usedByOthers;
          const need = updated.govt_fee * updated.quantity;
          // Credit card & creditor accounts may freely swing negative —
          // they're liability-side, so a higher debt is normal accounting.
          // Skip the toast + auto-clear for those.
          const skipGuard = account.type === 'credit_card' || account.type === 'creditor';
          if (!skipGuard && need > available) {
            toast.error('Insufficient Fund');
            updated.govt_fee_mode = '';
          }
        }
      }
      return updated;
    }));
  };

  const selectServiceForRow = (idx, svc) => {
    setItems(items.map((it, i) => {
      if (i !== idx) return it;
      const qty = it.quantity;
      const feeMode = svc.default_fee_mode || it.govt_fee_mode;
      const needsFollowup = !!svc.requires_followup;
      const workStatus = needsFollowup ? '' : 'completed';
      // Volume pricing — if the service defines tiers, pick the matching
      // per-unit price for the current qty. Price-based tiers use the
      // variant label that was clicked in the item picker (stashed on the
      // svc object as `_variant`).
      const variantLabel = svc._variant?.label || '';
      const svcCharge = variantLabel
        ? (Number(svc._variant.price) || 0)
        : _priceForQty(svc, qty);
      // Display name — append the variant to the service name so the row
      // and printout show "Photo Print — 5x7" instead of just "Photo Print".
      const displayName = variantLabel ? `${svc.name} — ${variantLabel}` : svc.name;
      return {
        ...it,
        service_id: svc.id,
        service_name: displayName,
        variant_label: variantLabel,
        service_charge: svcCharge,
        govt_fee: svc.govt_fee,
        govt_fee_mode: feeMode,
        total: qty * (svcCharge + svc.govt_fee),
        followup: needsFollowup,
        work_status: workStatus,
        // Recurring snapshot — frozen at the moment the service is picked.
        // Defaults `recurring_start_date` to today; operator can override
        // via the inline date picker that appears on the row.
        is_recurring: !!svc.is_recurring,
        recurring_interval: svc.recurring_interval || '',
        recurring_interval_months: svc.recurring_interval_months || null,
        recurring_start_date: svc.is_recurring ? (invoiceDate || new Date().toISOString().slice(0, 10)) : '',
      };
    }));
    // When the chosen service inherently requires follow-up, auto-open the
    // row's Work Status dropdown so staff pick the in-flight stage right
    // away instead of leaving it blank and tripping the Add-Item gate.
    if (svc.requires_followup) {
      setTimeout(() => {
        document.querySelector(`[data-testid="work-status-${idx}"]`)?.click();
      }, 80);
    }
    // For recurring services, pop the dates dialog so the operator can
    // confirm From date / view auto-filled To date right at the moment of
    // selection — keeps the subscription cadence honest from day one.
    if (svc.is_recurring) {
      // If we landed here via the Recurring → Renew flow, the URL carries
      // a `start_date` param (= the previous next-renewal anniversary).
      // Use that so the new subscription continues seamlessly from where
      // the old one ended instead of starting from today.
      const renewStart = searchParams.get('start_date');
      const initialFrom = renewStart || invoiceDate || new Date().toISOString().slice(0, 10);
      setRecurringDialog({ idx, service: svc, initialFrom });
    }
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, it) => s + it.total, 0);
  const totalSvcCharge = items.reduce((s, it) => s + (it.service_charge * it.quantity), 0);
  const totalGovtFee = items.reduce((s, it) => s + (it.govt_fee * it.quantity), 0);
  const discount = Math.max(0, Math.min(parseFloat(discountStr) || 0, subtotal));
  const total = Math.max(0, subtotal - discount);
  // `locked` = invoice already saved AND user is not in Edit-after-save mode.
  // Used to disable every row input + hide the Add Item / Remove buttons.
  const locked = !!(savedInvoice && !editingSavedInvoice);
  const amountPaid = parseFloat(amountPaidStr) || 0;
  // Cap requested advance to bill total (can't use more than the bill is)
  const effectiveAdvance = Math.min(advanceUsed || 0, total);
  // Total received toward invoice = cash + advance (clamped to total)
  const rawPaying = paymentType === 'full' ? total
                  : paymentType === 'credit' ? 0
                  : amountPaid + effectiveAdvance;
  const paying = Math.min(rawPaying, total);
  const balanceDue = total - paying;
  // Change = excess cash beyond what's owed after applying advance
  const cashOwedAfterAdvance = Math.max(total - effectiveAdvance, 0);
  const changeToReturn = paymentType === 'partial' ? Math.max(amountPaid - cashOwedAfterAdvance, 0) : 0;

  const handleSaveDraft = async () => {
    const validItems = items.filter(i => i.service_id);
    if (!customerId || validItems.length === 0) { toast.error('Select customer and at least one service'); return; }
    // Always prompt for a fresh reason — the staff member must justify why
    // the task is being parked. The previous reason (if any) is pre-filled
    // so editing an existing draft doesn't force a re-type.
    setShowDraftReasonPrompt(true);
  };

  const commitDraft = async () => {
    const trimmedReason = (draftReason || '').trim();
    if (!trimmedReason) { toast.error('Reason is required to save as draft'); return; }
    const validItems = items.filter(i => i.service_id);
    if (!customerId || validItems.length === 0) { toast.error('Select customer and at least one service'); return; }
    try {
      // Prepend the token reference to the draft notes so the admin can trace
      // the waiting-room origin even after the token chip disappears.
      const noteWithRef = tokenNumber && !(notes || '').includes(tokenNumber)
        ? `[${tokenNumber}] ${notes || ''}`.trim()
        : (notes || '');
      const body = {
        customer_id: customerId,
        customer_name: selectedCustomer?.name || '',
        applicant_name: (applicantName || '').trim(),
        items: validItems,
        notes: noteWithRef,
        reason: trimmedReason,
        // Preserve any user-typed invoice number on the draft so re-opening
        // it later restores the same serial in the form.
        invoice_number: manualInvoiceNo ? (previewInvoiceNumber || '').trim() : '',
      };
      if (currentDraftId) {
        await api.put(`/drafts/${currentDraftId}`, body);
        toast.success('Draft updated');
      } else {
        const r = await api.post('/drafts', body);
        // Track the new draft id so subsequent Save-as-Draft clicks on the
        // same page update this draft instead of creating a duplicate.
        if (r.data?.id) setCurrentDraftId(r.data.id);
        toast.success(tokenNumber ? `Saved as draft (ref ${tokenNumber})` : 'Saved as draft');
      }
      if (tokenId) {
        try { await api.delete(`/tokens/${tokenId}`); } catch { /* already gone, ignore */ }
        // Token has been converted into the draft — clear it from local state.
        setTokenId('');
        setTokenNumber('');
      }
      setShowDraftReasonPrompt(false);
      setCleanSig(JSON.stringify(validItems));
      // Stay on the invoice page so the user can keep editing. Only Cancel /
      // Close should return to the list.
    } catch { toast.error('Failed to save draft'); }
  };

  const handleSaveToken = async () => {
    const validItems = items.filter(i => i.service_id);
    if (!customerId) { toast.error('Select a customer'); return; }
    if (validItems.length === 0) { toast.error('Add at least one service'); return; }
    try {
      const body = {
        customer_id: customerId,
        customer_name: selectedCustomer?.name || '',
        applicant_name: (applicantName || '').trim(),
        items: validItems,
        notes,
        // When the user is in "convert draft" mode (draftId in URL) and
        // saves as a token, tell the backend so it can (a) stamp the draft
        // origin on the token for later round-trip restore, and (b) remove
        // the draft from the Drafts list immediately.
        from_draft_id: currentDraftId || '',
      };
      if (tokenId) {
        await api.put(`/tokens/${tokenId}`, body);
        toast.success(`Token ${tokenNumber} updated`);
      } else {
        const r = await api.post('/tokens', body);
        toast.success(`Saved as ${r.data.token_number}`);
      }
      goBack();
    } catch { toast.error('Failed to save token'); }
  };

  const [negAccounts, setNegAccounts] = useState(null);

  /**
   * Auto-recover from a 409 "Invoice number ... is already in use" response
   * by re-fetching the next available auto-number and retrying the save once.
   * The user sees a single warning toast so they know which number ended up
   * being used. `apiFn(invoiceNumberToUse)` performs the actual POST/PUT.
   */
  const saveWithDupRetry = async (apiFn) => {
    // Auto mode sends '' so the server assigns last-generated + 1 at save time.
    const desired = manualInvoiceNo ? (previewInvoiceNumber || '').trim() : '';
    try {
      return await apiFn(desired);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const is409Clash = err?.response?.status === 409 && /already in use/i.test(detail);
      if (!is409Clash) throw err;
      let nextNo = '';
      try {
        const r = await api.get('/invoices/next-number');
        nextNo = (r.data?.invoice_number || '').trim();
      } catch { /* fall through — empty string lets the backend auto-seq it */ }
      const result = await apiFn(nextNo);
      setPreviewInvoiceNumber(nextNo);
      toast.warning(`Invoice number ${desired} was already taken — saved as ${nextNo || 'the next available number'}.`);
      return result;
    }
  };

  const handleSubmit = async () => {
    if (!customerId) { toast.error('Select a customer'); return; }
    if (items.length === 0 || !items.some(i => i.service_id)) { toast.error('Add at least one service'); return; }
    const validItems = items.filter(i => i.service_id);

    // Block save if any item has a govt fee > 0 without a fee mode selected
    const missingFeeMode = [];
    items.forEach((it, idx) => {
      if (it.service_id && (it.govt_fee || 0) > 0 && !it.govt_fee_mode) {
        missingFeeMode.push(idx + 1);
      }
    });
    if (missingFeeMode.length > 0) {
      const rows = missingFeeMode.join(', ');
      toast.error(`Please select Fee Mode for row${missingFeeMode.length > 1 ? 's' : ''} ${rows} (govt fee is non-zero).`);
      return;
    }

    // Hard-block: any fee-mode account that would go negative
    const deductions = {};
    validItems.forEach(it => {
      if (it.govt_fee > 0 && it.govt_fee_mode) {
        deductions[it.govt_fee_mode] = (deductions[it.govt_fee_mode] || 0) + it.govt_fee * it.quantity;
      }
    });
    const negatives = [];
    Object.entries(deductions).forEach(([id, ded]) => {
      const acc = finAccounts.find(a => a.id === id);
      if (!acc) return;
      // Credit cards & creditors may freely swing negative — exclude them
      // from the pre-save guard.
      if (acc.type === 'credit_card' || acc.type === 'creditor') return;
      if (acc.balance < ded) negatives.push({ name: acc.name, current: acc.balance, deduction: ded });
    });
    if (negatives.length > 0) {
      setNegAccounts(negatives);
      return;
    }

    setSubmitting(true);
    try {
      const followup = validItems.some(i => i.followup);
      // NEW FLOW: every Create Invoice saves as CREDIT (no payment yet). The
      // user records the payment via the Pay button → PaymentReceiptDialog.
      const created = await saveWithDupRetry(async (invNo) => {
        const { data } = await api.post('/invoices', {
          customer_id: customerId, items: validItems,
          applicant_name: (applicantName || '').trim(),
          // Pass through the user-typed (or draft-restored) invoice number.
          // Empty string falls back to the server's auto-sequence.
          invoice_number: invNo,
          payment_mode: paymentMode || (finAccounts.find(a => a.type === 'cash')?.id || ''),
          payment_type: 'credit', amount_paid: 0,
          notes, followup, invoice_date: invoiceDate,
          // no payment_date at creation time — the receipt popup will carry it
          excess_handling: 'change', excess_amount: 0,
          advance_used: 0, discount,
          reference_number: tokenNumber || '', from_token_id: tokenId || null,
        });
        return data;
      });
      if (currentDraftId) { await api.delete(`/drafts/${currentDraftId}`).catch(() => {}); }
      toast.success(`${created.invoice_number} created (Credit). Click "Pay" to record payment.`);
      setSavedInvoice(created);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create invoice'); }
    finally { setSubmitting(false); }
  };

  // Update an already-saved invoice while staying on the same page. The
  // backend PUT endpoint recomputes status/balance off the new items and
  // also handles the rare case where the new total is now < amount_paid.
  const handleUpdateInvoice = async () => {
    if (!savedInvoice) return;
    const validItems = items.filter(i => i.service_id);
    if (!customerId) { toast.error('Customer required'); return; }
    if (validItems.length === 0) { toast.error('At least one service required'); return; }
    setSubmitting(true);
    try {
      const followup = validItems.some(i => i.followup);
      await api.put(`/invoices/${savedInvoice.id}`, {
        customer_id: customerId, items: validItems,
        applicant_name: (applicantName || '').trim(),
        payment_mode: savedInvoice.payment_mode || (finAccounts.find(a => a.type === 'cash')?.id || ''),
        payment_type: savedInvoice.payment_type || 'credit',
        amount_paid: savedInvoice.amount_paid || 0,
        notes, followup, invoice_date: savedInvoice.invoice_date || invoiceDate,
        excess_handling: 'change', excess_amount: 0,
        advance_used: 0, discount,
      });
      // Backend PUT returns only `{message: "..."}` — re-fetch the invoice so
      // derived fields (`balance_due`, `status`, `payment_history`) hydrate
      // correctly. Without this, `savedInvoice` was being clobbered with the
      // bare message dict, which made the Due row drop to 0 and the success
      // toast read "undefined updated".
      const { data: updated } = await api.get(`/invoices/${savedInvoice.id}`);
      toast.success(`${updated.invoice_number} updated`);
      setSavedInvoice(updated);
      setEditingSavedInvoice(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update invoice'); }
    finally { setSubmitting(false); }
  };

  // Print uses the shared `printInvoice` helper (`lib/printInvoice.js`) so
  // both the pre-save preview (staff peeking at the bill before saving) and
  // the post-save print use the same layout as Invoices → Actions → Print.
  const handlePrint = async () => {
    const validItems = items.filter(i => i.service_id);
    await sharedPrintInvoice({
      invoice: {
        // Post-save: `savedInvoice` carries the persisted totals + number.
        // Pre-save: build a preview-only object from local state.
        invoice_number: savedInvoice?.invoice_number || previewInvoiceNumber || '(auto on save)',
        created_at: savedInvoice?.created_at || invoiceDate,
        invoice_date: invoiceDate,
        customer_id: customerId,
        customer_name: customers.find(c => c.id === customerId)?.name || '—',
        applicant_name: applicantName,
        items: validItems,
        discount: discount || 0,
        total: savedInvoice?.total ?? total,
        amount_paid: savedInvoice?.amount_paid ?? paying,
        balance_due: savedInvoice?.balance_due ?? balanceDue,
        payment_mode: paymentMode,
      },
      finAccounts,
      customer: customers.find(c => c.id === customerId),
    });
  };

  const handleAddCustomer = async (force = false) => {
    if (!custForm.name || !custForm.phone) { toast.error('Name and phone required'); return; }
    try {
      const { data } = await api.post(`/customers${force ? '?force=true' : ''}`, custForm);
      toast.success('Customer added');
      setShowAddCustomer(false);
      setCustForm({ name: '', phone: '', address: '', notes: '' });
      fetchData();
      if (data.id) setCustomerId(data.id);
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(err.response.data.detail)) {
          handleAddCustomer(true);
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to add customer');
      }
    }
  };

  // After a quick-add Service from the row combobox, refresh the catalog
  // and auto-select the newly created service in the originating row.
  const handleServiceCreated = async (data) => {
    const rowIdx = addServiceForRow;
    setAddServiceForRow(-1);
    setAddServiceName('');
    const svcRes = await api.get('/services', { params: { include_archived: false } });
    setServices(svcRes.data);
    if (rowIdx >= 0 && data?.id) {
      const newSvc = svcRes.data.find(s => s.id === data.id);
      if (newSvc) selectServiceForRow(rowIdx, newSvc);
    }
  };

  const handleReceiveDue = async () => {
    if (!showPayDue || !duePayAmount || parseFloat(duePayAmount) <= 0 || !duePayMode) { toast.error('Fill all fields'); return; }
    try {
      await api.put(`/invoices/${showPayDue.invoice_id}/payment`, {
        amount: parseFloat(duePayAmount), payment_mode: duePayMode, payment_date: duePayDate
      });
      toast.success('Payment recorded');
      setShowPayDue(null);
      setDuePayAmount('');
      setDuePayDate(new Date().toISOString().split('T')[0]);
      // Re-fetch ledger
      if (customerId) {
        const r = await api.get(`/customers/${customerId}/ledger`);
        const entries = r.data;
        const totalBilled = entries.reduce((s, e) => s + (e.debit || 0), 0);
        const totalReceived = entries.reduce((s, e) => s + (e.credit || 0), 0);
        setCustLedger({ entries, totalBilled, totalReceived, pendingDue: totalBilled - totalReceived });
      }
    } catch { toast.error('Payment failed'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="create-invoice-page">
      <style>{`input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; } input[type=number] { -moz-appearance: textfield; }`}</style>

      <div className="flex items-center gap-2 mb-4 justify-between flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => confirmLeave() && goBack()} className="rounded-lg" data-testid="back-btn"><ArrowLeft size={18} /></Button>
        {/* Quick Finance Actions — fund transfer / receipt / income /
            expense without losing the invoice draft. Each opens a full-page
            popup; saving auto-closes and brings us back here. */}
        <div className="flex items-center gap-2 flex-wrap">
          {savedInvoice && (
            <Button size="sm" variant="outline" onClick={() => window.location.assign('/invoices/create')}
              className="rounded-lg h-8 text-xs border-[#3B82F6] text-[#2563EB] hover:bg-[#EFF6FF]"
              title="Create new invoice (Alt+N)"
              data-testid="quick-new-invoice-btn">
              <Plus size={14} className="mr-1" /> Create Invoice
              <span className="ml-2 text-[10px] font-mono text-[#A1A1AA]">Alt+N</span>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('transfer')}
            className="rounded-lg h-8 text-xs border-[#F59E0B] text-[#B45309] hover:bg-[#FEF3C7]"
            data-testid="quick-transfer-btn">
            <ArrowsLeftRight size={14} className="mr-1" /> Transfer
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('receipt')}
            className="rounded-lg h-8 text-xs border-[#10B981] text-[#10B981] hover:bg-[#ECFDF5]"
            data-testid="quick-receipt-btn">
            <Receipt size={14} className="mr-1" /> Receipt
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('income')}
            className="rounded-lg h-8 text-xs border-[#10B981] text-[#10B981] hover:bg-[#ECFDF5]"
            data-testid="quick-income-btn">
            <ArrowDown size={14} className="mr-1" /> Income
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('expense')}
            className="rounded-lg h-8 text-xs border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]"
            data-testid="quick-expense-btn">
            <ArrowUp size={14} className="mr-1" /> Payment
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Customer + Ledger Summary + Invoice#/Date (fixed-width on right) */}
        <div className="flex flex-col lg:flex-row gap-4">
          <Card className="rounded-xl border-[#E4E4E7] shadow-sm lg:basis-1/2 lg:flex-shrink-0">
            <CardContent className="p-4 h-full flex flex-col justify-center">
              {/* Customer picker (half width) + Applicant textbox (other half).
                  Applicant defaults to the selected customer's name; the
                  operator only touches it when service is being done for a
                  different person (family member, delegate, scheme applicant). */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] font-bold text-[#0A0A0A]">Customer *</Label>
                  <div className="mt-1">
                    <CustomerCombo
                      triggerRef={customerTriggerRef}
                      customers={[...customers].sort((a, b) => a.name.localeCompare(b.name))}
                      value={customerId}
                      onSelect={id => {
                        setCustomerId(id);
                        // Sync Applicant when it was equal to the previous
                        // customer name (i.e. still on the default). Preserve
                        // any manual override the operator has already typed.
                        const prev = customers.find(c => c.id === customerId)?.name || '';
                        const next = customers.find(c => c.id === id)?.name || '';
                        setApplicantName(a => (!a || a === prev) ? next : a);
                      }}
                      onAddNew={(q) => {
                        const trimmed = (q || '').trim();
                        const isPhone = /^[+]?\d[\d\s-]*$/.test(trimmed);
                        setCustForm({ name: isPhone ? '' : trimmed, phone: isPhone ? trimmed : '', address: '', notes: '' });
                        setShowAddCustomer(true);
                      }}
                      disabled={locked}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] font-bold text-[#0A0A0A]">Applicant</Label>
                  <Input
                    value={applicantName}
                    onChange={e => setApplicantName(e.target.value)}
                    placeholder={selectedCustomer?.name || 'Applicant name'}
                    className="rounded-lg mt-1 h-9 text-sm"
                    disabled={locked}
                    data-testid="applicant-name-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ledger Summary — fills the middle space (only when a customer is selected) */}
          {selectedCustomer && custLedger && (
            <Card
              className={`rounded-xl shadow-sm flex-1 min-w-0 ${custLedger.pendingDue > 0 ? 'border-[#FCA5A5] bg-[#FEF2F2]' : 'border-[#BBF7D0] bg-[#F0FDF4]'}`}
              data-testid="customer-summary-card"
              tabIndex={-1}
            >
              <CardContent className="p-3 h-full flex flex-col justify-center">
                <div className="flex items-start justify-between gap-3">
                  {/* LEFT — customer details (top → bottom) + action icons */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-semibold text-[#0A0A0A] truncate" title={selectedCustomer.name}>{selectedCustomer.name}</p>
                    <p className="text-[11px] text-[#52525B] truncate font-mono mt-0.5" title={selectedCustomer.phone || ''}>
                      {selectedCustomer.phone || <span className="text-[#A1A1AA] italic font-sans">No contact</span>}
                    </p>
                    <p className="text-[11px] text-[#52525B] truncate mt-0.5" title={selectedCustomer.address || ''}>
                      {selectedCustomer.address || <span className="text-[#A1A1AA] italic">No address</span>}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-[#3B82F6] hover:text-[#2563EB] hover:bg-[#EFF6FF]"
                        onClick={() => setShowCredentials(true)}
                        title="View / edit saved login IDs"
                        data-testid="view-credentials-btn"
                      >
                        <LockKey size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-[#3B82F6] hover:text-[#2563EB] hover:bg-[#EFF6FF]"
                        onClick={() => setShowEditCustomer(true)}
                        title="Edit customer details"
                        data-testid="edit-customer-btn"
                      >
                        <PencilSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-[#3B82F6] hover:text-[#2563EB]" onClick={() => setShowLedgerPopup(true)} data-testid="view-ledger-btn">
                        View Ledger
                      </Button>
                    </div>
                  </div>
                  {/* RIGHT — Billed / Received / Due stacked top-to-bottom */}
                  <div className="text-right shrink-0 space-y-1">
                    <div>
                      <span className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA] mr-2">Billed</span>
                      <span className="text-sm font-bold font-mono">{fmt(custLedger.totalBilled)}</span>
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA] mr-2">Received</span>
                      <span className="text-sm font-bold font-mono text-[#10B981]">{fmt(custLedger.totalReceived)}</span>
                    </div>
                    {(selectedCustomer.advance_balance || 0) > 0 ? (
                      <div>
                        <span className="text-[9px] uppercase tracking-[0.12em] text-[#3B82F6] mr-2">Advance Held</span>
                        <span className="text-sm font-bold font-mono text-[#3B82F6]" data-testid="customer-advance-held">{fmt(selectedCustomer.advance_balance)}</span>
                      </div>
                    ) : (
                      <div>
                        <span className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA] mr-2">Due</span>
                        <span className={`text-sm font-bold font-mono ${custLedger.pendingDue > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(custLedger.pendingDue)}</span>
                      </div>
                    )}
                  </div>
                </div>
                {(selectedCustomer.advance_balance || 0) > 0 && effectiveAdvance > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E4E4E7]/70 flex items-center justify-end gap-2">
                    {effectiveAdvance > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#3B82F6] font-semibold">Applied: {fmt(effectiveAdvance)}</span>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[9px] text-[#52525B] hover:text-[#0A0A0A]"
                          onClick={() => setAdvanceUsed(0)} data-testid="clear-advance-btn">
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Invoice# (top) + Date (bottom) — dedicated card with fixed width */}
          <Card
            className="rounded-xl border-[#E4E4E7] shadow-sm lg:w-64 lg:flex-shrink-0 lg:ml-auto"
            data-testid="invoice-meta-card"
            tabIndex={-1}
          >
            <CardContent className="p-4 h-full flex flex-col justify-center gap-3">
              {(tokenId || savedInvoice || draftPreservedNo) && (
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#A1A1AA]">{tokenId ? 'Ref / Token' : 'Invoice #'}</p>
                {savedInvoice ? (
                  <p className="text-base font-mono font-bold text-[#3B82F6]" data-testid="preview-invoice-number">{savedInvoice.invoice_number}</p>
                ) : tokenId ? (
                  <p className="text-base font-mono font-bold text-[#3B82F6]" data-testid="preview-invoice-number">
                    {tokenNumber}
                  </p>
                ) : manualInvoiceNo ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={previewInvoiceNumber}
                      onChange={e => setPreviewInvoiceNumber(e.target.value)}
                      placeholder="—"
                      autoFocus
                      className="rounded-lg h-8 text-sm w-28 font-mono font-bold text-[#3B82F6] text-right border-[#3B82F6]"
                      data-testid="preview-invoice-number"
                    />
                    <button type="button" onClick={useAutoInvoiceNo} title="Back to auto numbering" className="text-[10px] font-semibold text-[#52525B] hover:text-[#3B82F6] underline-offset-2 hover:underline" data-testid="invoice-number-auto-btn">Auto</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <p className="text-base font-mono font-bold text-[#3B82F6]" title="Assigned automatically on save (last number + 1)" data-testid="preview-invoice-number">
                      {previewInvoiceNumber || '—'}
                    </p>
                    <button type="button" onClick={() => setManualInvoiceNo(true)} title="Set invoice number manually" className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[#A1A1AA] hover:text-[#3B82F6] hover:bg-[#EFF6FF]" data-testid="invoice-number-manual-btn">
                      <PencilSimple size={13} />
                    </button>
                  </div>
                )}
              </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#A1A1AA]">Date</Label>
                {dateUnlocked ? (
                  <Input type="date" value={invoiceDate} max={todayIso} autoFocus onChange={e => { setInvoiceDate(e.target.value); setPaymentDate(e.target.value); }}
                    onBlur={() => setDateUnlocked(false)}
                    className="rounded-lg h-8 text-sm w-36" data-testid="invoice-date-input" />
                ) : (
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-mono font-semibold text-[#0A0A0A]" data-testid="invoice-date-locked">
                      {invoiceDate ? new Date(invoiceDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </p>
                    <button type="button" onClick={() => setDateUnlocked(true)} title="Change invoice date" className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[#A1A1AA] hover:text-[#3B82F6] hover:bg-[#EFF6FF]" data-testid="invoice-date-edit-btn">
                      <PencilSimple size={13} />
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

          {/* Items */}
          <div>
            <p className="text-base font-semibold mb-3">Items *</p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-[#F8FAFC] z-10">
                    <tr className="border-b-2 border-[#E4E4E7]">
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-left pb-2 pr-2 w-56">Item</th>
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-12">Qty</th>
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-right pb-2 px-1 w-[70px]">Svc Charge</th>
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-right pb-2 px-1 w-[80px]">Govt Fee</th>
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-28">Fee Mode</th>
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-28">Work Status</th>
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-right pb-2 px-1 w-20">Total</th>
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-left pb-2 px-1 w-28">Remarks</th>
                      {canAssign && (
                        <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-24">Assigned To</th>
                      )}
                      <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-16">Follow-up</th>
                      <th className="w-8 pb-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx} className="border-b border-[#F4F4F5]">
                        <td className="py-1.5 pr-2">
                          {(() => {
                            // Phase D — link1 icon on Invoice page. Only
                            // shown when the picked service has a non-empty
                            // link1. Opens in a new tab.
                            const svc = services.find(s => s.id === it.service_id);
                            return svc?.link1 ? (
                              <a href={svc.link1} target="_blank" rel="noreferrer"
                                title={`Open ${svc.name} portal`}
                                data-testid={`item-link1-${idx}`}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#E0F2FE] text-[#0369A1] hover:bg-[#BAE6FD] mr-1.5 align-middle">
                                <LinkSimple size={13} weight="bold" />
                              </a>
                            ) : null;
                          })()}
                          <div className={services.find(s => s.id === it.service_id)?.link1 ? 'inline-block align-middle w-[calc(100%-2.25rem)]' : 'block'}>
                          <ServiceCombo
                            services={[...services].sort((a, b) => a.name.localeCompare(b.name))}
                            value={it.service_id}
                            onSelect={s => selectServiceForRow(idx, s)}
                            onAddNew={(q) => { setAddServiceForRow(idx); setAddServiceName(q || ''); setShowAddService(true); }}
                            testId={`service-select-${idx}`}
                            disabled={locked}
                          />
                          </div>
                        </td>
                        <td className="py-1.5 px-1">
                          <Input type="number" min={1} value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                            disabled={locked}
                            style={noSpinStyle} className="w-12 text-center rounded-lg h-9 text-sm disabled:opacity-70" data-testid={`qty-input-${idx}`} />
                        </td>
                        <td className="py-1.5 px-1">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A1A1AA]">&#8377;</span>
                            <Input type="number" value={it.service_charge} onChange={e => updateItem(idx, 'service_charge', e.target.value)}
                              disabled={locked}
                              style={noSpinStyle} className="w-[70px] text-right rounded-lg h-9 text-sm pl-5 font-mono disabled:opacity-70" data-testid={`svc-charge-${idx}`} />
                          </div>
                        </td>
                        <td className="py-1.5 px-1">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A1A1AA]">&#8377;</span>
                            <Input type="number" value={it.govt_fee} onChange={e => updateItem(idx, 'govt_fee', e.target.value)}
                              disabled={locked}
                              style={noSpinStyle} className="w-[80px] text-right rounded-lg h-9 text-sm pl-5 font-mono disabled:opacity-70" data-testid={`govt-fee-${idx}`} />
                          </div>
                        </td>
                        <td className="py-1.5 px-1">
                          <Select value={it.govt_fee_mode} onValueChange={v => updateItem(idx, 'govt_fee_mode', v)} disabled={locked || !it.govt_fee || it.govt_fee === 0}>
                            <SelectTrigger className={`rounded-lg h-9 text-sm w-28 ${(!it.govt_fee || it.govt_fee === 0) ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid={`fee-mode-${idx}`}>
                              <SelectValue>
                                {it.govt_fee_mode ? (() => {
                                  const acc = finAccounts.find(a => a.id === it.govt_fee_mode);
                                  return acc ? `${acc.name}` : '';
                                })() : ''}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {finAccounts.filter(a => a.type === 'cash' || a.type === 'bank' || a.type === 'wallet' || a.type === 'creditor' || (a.type === 'credit_card' && (a.balance || 0) <= 0)).map(a => {
                                const available = getAvailableBalance(a.id, idx);
                                const rowGovtFee = (it.govt_fee || 0) * (it.quantity || 1);
                                // Credit cards & creditor accounts are allowed to swing further
                                // negative — never disabled by the insufficient-balance check.
                                const isInsufficient = a.type !== 'credit_card' && a.type !== 'creditor' && available < rowGovtFee && rowGovtFee > 0;
                                return (
                                  <SelectItem key={a.id} value={a.id} disabled={isInsufficient}>
                                    <span className={`flex items-center justify-between gap-2 w-full ${isInsufficient ? 'opacity-50' : ''}`}>
                                      <span>{a.name} <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">({(a.type || '').replace('_', ' ')})</span></span>
                                      <span className={`text-xs font-mono ${isInsufficient ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(available)}</span>
                                    </span>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-1.5 px-1">
                          <Select value={it.work_status} onValueChange={v => updateItem(idx, 'work_status', v)} disabled={locked}>
                            <SelectTrigger className={`rounded-lg h-9 text-sm w-28 disabled:opacity-70 ${it.work_status === 'completed' ? 'text-[#10B981] border-[#10B981]/30' : it.work_status === 'in_process' ? 'text-[#3B82F6] border-[#3B82F6]/30' : 'text-[#F59E0B] border-[#F59E0B]/30'}`} data-testid={`work-status-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="completed">Submitted</SelectItem>
                              <SelectItem value="in_process">Preparing</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-1.5 px-1 text-right font-mono font-bold text-sm">{fmt(it.total)}</td>
                        <td className="py-1.5 px-1">
                          <Input value={it.remark || ''} onChange={e => updateItem(idx, 'remark', e.target.value)}
                            disabled={locked}
                            placeholder="..." className="w-28 rounded-lg h-9 text-xs disabled:opacity-70" data-testid={`remark-${idx}`} />
                        </td>
                        {canAssign && (
                          <td className="py-1.5 px-1">
                            {/* Assignee dropdown — Self / Unassigned / staff list. */}
                            <Select value={it.assigned_to || ''} onValueChange={v => updateItem(idx, 'assigned_to', v === '__unassigned__' ? '' : v)} disabled={locked}>
                              <SelectTrigger className="rounded-lg h-9 text-xs px-2" data-testid={`assignee-${idx}`}>
                                <SelectValue placeholder="Unassigned" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="self">Self</SelectItem>
                                <SelectItem value="__unassigned__">Unassigned</SelectItem>
                                {staffDirectory.filter(s => s.id !== user?.id).map(s => (
                                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        )}
                        <td className="py-1.5 px-1 text-center">
                          <input type="checkbox" checked={!!it.followup} onChange={e => updateItem(idx, 'followup', e.target.checked)}
                            disabled={locked}
                            tabIndex={-1}
                            className="rounded h-4 w-4 cursor-pointer accent-[#3B82F6] disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`item-followup-${idx}`} />
                        </td>
                        <td className="py-1.5 pl-1">
                          {!locked && (
                            <Button variant="ghost" size="sm" tabIndex={-1} className="h-7 w-7 p-0 text-[#EF4444]" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}><Trash size={14} /></Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center mt-4 pt-4 border-t-2 border-[#E4E4E7] flex-wrap gap-2">
                  {/* `locked` = invoice already saved AND user is not in
                      Edit-after-save mode. While locked we hide the row
                      actions and the Add Item button so the bill reads as a
                      finalised document. */}
                  {(() => null)()}
                  {(!savedInvoice || editingSavedInvoice) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addEmptyItem}
                      disabled={!canAddItem()}
                      className="rounded-lg h-9 text-sm border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF] disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="add-service-row-btn"
                      title={canAddItem() ? 'Add Item (Alt+I)' : 'Pick a work status for the current item first'}
                    >
                      <Plus size={16} className="mr-1" /> Add Item
                      <span className="ml-2 text-[10px] font-mono text-[#A1A1AA]">Alt+I</span>
                    </Button>
                  )}
                  {!savedInvoice && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSaveToken}
                        className="rounded-lg h-9 text-sm bg-gradient-to-r from-[#10B981] to-[#059669] text-white hover:opacity-90 hover:text-white"
                        data-testid="save-token-btn"
                      >
                        <Ticket size={15} weight="fill" className="mr-1.5" />
                        {tokenId ? `Update ${tokenNumber}` : 'Save as Token'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSaveDraft}
                        className="rounded-lg h-9 text-sm border-[#F59E0B] text-[#F59E0B] hover:bg-[#FFFBEB]"
                        data-testid="save-draft-btn"
                      >
                        Save as Draft
                      </Button>
                    </>
                  )}

                  {/* Spacer pushes Create / Cancel (or Pay / Print / Close) to the right */}
                  <div className="flex-1 min-w-[40px]" />

                  {!savedInvoice ? (
                    <>
                      <Button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="rounded-lg h-9 px-5 text-sm font-semibold shadow-sm bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white hover:opacity-90 hover:text-white"
                        data-testid="submit-invoice-btn"
                      >
                        <Receipt size={16} className="mr-2" />
                        {submitting ? 'Creating...' : (tokenId ? `Save ${tokenNumber} as Invoice` : 'Create Invoice')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => confirmLeave() && goBack()}
                        className="rounded-lg h-9 text-sm border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#0A0A0A]"
                        data-testid="cancel-invoice-btn"
                      >
                        {currentDraftId ? 'Close' : 'Cancel'}
                      </Button>

                      {/* Discount + Remarks — always visible while composing. */}
                      <div className="ml-2 relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#A1A1AA]">&#8377;</span>
                        <Input type="number" value={discountStr} onChange={e => setDiscountStr(e.target.value)}
                          placeholder="Discount" style={noSpinStyle} className="rounded-lg h-9 text-sm pl-6 font-mono w-24 border-[#F59E0B] focus-visible:ring-[#F59E0B]" data-testid="discount-input" />
                      </div>
                      <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Invoice remarks..."
                        className="rounded-lg h-9 text-sm w-44" data-testid="invoice-notes-input" />
                    </>
                  ) : editingSavedInvoice ? (
                    <>
                      {/* Edit mode after save — Update / Cancel Edit + Discount + Remarks. */}
                      <Button
                        onClick={handleUpdateInvoice}
                        disabled={submitting}
                        className="rounded-lg h-9 px-5 text-sm font-semibold shadow-sm bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white hover:opacity-90 hover:text-white"
                        data-testid="update-invoice-btn"
                      >
                        <Receipt size={16} className="mr-2" />
                        {submitting ? 'Updating...' : 'Update Invoice'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingSavedInvoice(false)}
                        className="rounded-lg h-9 text-sm border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#0A0A0A]"
                        data-testid="cancel-edit-invoice-btn"
                      >
                        Cancel Edit
                      </Button>
                      <div className="ml-2 relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#A1A1AA]">&#8377;</span>
                        <Input type="number" value={discountStr} onChange={e => setDiscountStr(e.target.value)}
                          placeholder="Discount" style={noSpinStyle} className="rounded-lg h-9 text-sm pl-6 font-mono w-24 border-[#F59E0B] focus-visible:ring-[#F59E0B]" data-testid="discount-input" />
                      </div>
                      <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Invoice remarks..."
                        className="rounded-lg h-9 text-sm w-44" data-testid="invoice-notes-input" />
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => setShowPayDialog(true)}
                        disabled={savedInvoice.balance_due <= 0}
                        className="rounded-lg h-9 px-5 text-sm font-semibold shadow-sm bg-gradient-to-r from-[#10B981] to-[#059669] text-white hover:opacity-90 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="pay-saved-btn"
                      >
                        <CurrencyInr size={16} className="mr-1.5" /> Pay
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingSavedInvoice(true)}
                        className="rounded-lg h-9 text-sm border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]"
                        data-testid="edit-saved-btn"
                      >
                        <PencilSimple size={16} className="mr-1.5" /> Edit Invoice
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrint}
                        className="rounded-lg h-9 text-sm border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]"
                        data-testid="print-saved-btn"
                      >
                        <Printer size={16} className="mr-1.5" /> Print
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => confirmLeave() && goBack()}
                        className="rounded-lg h-9 text-sm border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#0A0A0A]"
                        data-testid="done-btn"
                      >
                        Close
                      </Button>
                      {/* Delete the just-saved invoice — only available when
                          no receipts have been attached yet. Once an
                          adjustment / payment exists, the user must delete
                          those rows first. */}
                      {(savedInvoice.payment_history || []).length === 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteSavedInvoice}
                          className="rounded-lg h-9 text-sm border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                          data-testid="delete-saved-invoice-btn"
                        >
                          <Trash size={16} className="mr-1.5" /> Delete
                        </Button>
                      )}
                    </>
                  )}

                  {/* Right-aligned 2-line totals block (Svc/Govt on top, Total below) */}
                  <div className="ml-3 pl-3 border-l border-[#E4E4E7] flex flex-col items-end justify-center h-9 leading-tight" data-testid="action-row-summary">
                    <div className="text-[11px] text-[#52525B] flex items-center gap-2">
                      <span>Svc: <span className="font-mono font-semibold">{fmt(totalSvcCharge)}</span></span>
                      <span>Govt: <span className="font-mono font-semibold">{fmt(totalGovtFee)}</span></span>
                      {discount > 0 && (
                        <span className="text-[#F59E0B]">Disc: <span className="font-mono font-semibold">−{fmt(discount)}</span></span>
                      )}
                    </div>
                    <div className="text-lg font-extrabold leading-none mt-0.5">Total: <span className="font-mono">{fmt(total)}</span></div>
                  </div>
                </div>

                {/* Receipts list — placed BELOW the action row so the amount column
                    lines up under the Total head in the summary block above.
                    Mirrors the Edit Invoice page. */}
                <div className="border-t-2 border-[#E4E4E7] mt-3 pt-3" data-testid="receipts-section">
                  {(() => {
                    const history = savedInvoice?.payment_history || [];
                    if (history.length === 0) {
                      return <p className="text-xs text-[#A1A1AA] italic text-right">No receipts recorded yet.</p>;
                    }
                    return (
                      <div className="space-y-1">
                        {history.map((rcp, i) => {
                          const acc = finAccounts.find(a => a.id === rcp.payment_mode);
                          const dt = rcp.payment_date || rcp.date || rcp.created_at;
                          const dateStr = dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
                          const isAdv = !!rcp.from_advance;
                          // Single-entry rule: total cash received = bill portion +
                          // any overpayment that was parked as customer credit on
                          // the same receipt. Surface the FULL amount as one row.
                          const fullAmt = (rcp.amount || 0) + (rcp.overpay_amount || 0);
                          return (
                            <div key={rcp.receipt_number || i} className="flex items-center justify-end gap-4 text-xs text-[#52525B]" data-testid={`receipt-row-${i}`}>
                              <span className="text-[#71717A]">{dateStr}</span>
                              <span className="font-mono font-semibold text-[#10B981]">{rcp.receipt_number || '—'}</span>
                              <span className={`uppercase tracking-wider text-[10px] ${isAdv ? 'text-[#3B82F6] font-semibold' : 'text-[#52525B]'}`}>
                                {isAdv ? 'Adj. with Advance' : (acc ? `${acc.name} · ${acc.type}` : (rcp.payment_mode || '—'))}
                              </span>
                              <span className="font-mono font-bold text-sm text-[#10B981] min-w-[80px] text-right">{fmt(fullAmt)}</span>
                              <button
                                type="button"
                                onClick={() => handleDeleteSavedReceipt(rcp.receipt_number)}
                                className="text-[#EF4444] hover:text-[#DC2626] p-1"
                                title={`Delete ${rcp.receipt_number || 'this receipt'}`}
                                data-testid={`delete-receipt-${rcp.receipt_number}`}
                              >
                                <Trash size={13} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Unadjusted receipts panel — between recorded receipts and
                    the Due row. Each row carries an Add button so staff can
                    apply that specific receipt's leftover credit toward the
                    current bill in one click. Any excess stays in the pool. */}
                {savedInvoice && (savedInvoice.balance_due ?? 0) > 0 && unadjusted.receipts.length > 0 && (
                  <div className="border-t border-[#E4E4E7] mt-3 pt-3" data-testid="unadjusted-receipts-panel">
                    <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#3B82F6] mb-2">
                      Available credit from earlier receipts
                    </p>
                    <div className="space-y-1">
                      {unadjusted.receipts.map((rcp, i) => {
                        const dt = rcp.date ? new Date(rcp.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
                        const due = savedInvoice.balance_due ?? 0;
                        const applyAmt = Math.min(rcp.unapplied_amount || 0, due);
                        const busy = applyingReceiptId === rcp.txn_id;
                        const appliedTo = rcp.applied_to || [];
                        return (
                          <div key={rcp.txn_id} className="flex flex-col items-end gap-0.5" data-testid={`unadjusted-row-${i}`}>
                            <div className="flex items-center justify-end gap-3 text-xs text-[#52525B]">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleApplyUnadjustedReceipt(rcp)}
                                disabled={busy || applyAmt <= 0}
                                className="h-7 px-3 text-[10px] bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-md font-semibold disabled:opacity-50"
                                data-testid={`unadjusted-add-${rcp.txn_id}`}
                                title={`Adjust ${fmt(applyAmt)} from ${rcp.voucher_number || 'this receipt'}`}
                              >
                                <Plus size={11} className="mr-1" /> {busy ? 'Adding…' : `Add ${fmt(applyAmt)}`}
                              </Button>
                              <span className="text-[#71717A]">{dt}</span>
                              <span className="font-mono font-semibold text-[#3B82F6]">{rcp.voucher_number || '—'}</span>
                              <span className="uppercase tracking-wider text-[10px] text-[#3B82F6]">Unadjusted</span>
                              <span className="font-mono font-bold text-sm text-[#3B82F6] min-w-[80px] text-right">{fmt(rcp.unapplied_amount || 0)}</span>
                            </div>
                            {appliedTo.length > 0 && (
                              <div className="text-[10px] text-[#71717A] flex flex-wrap items-center justify-end gap-1.5" data-testid={`unadjusted-applied-to-${rcp.txn_id}`}>
                                <span className="text-[#A1A1AA]">used in</span>
                                {appliedTo.map((a, idx) => (
                                  <span key={a.invoice_id} className="font-mono">
                                    <span className="text-[#3B82F6]">{a.invoice_number}</span>
                                    <span className="text-[#A1A1AA] ml-1">({fmt(a.amount)})</span>
                                    {idx < appliedTo.length - 1 && <span className="text-[#A1A1AA]">,</span>}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="border-t-2 border-[#E4E4E7] mt-3 pt-3 flex items-center justify-end gap-3" data-testid="due-row">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Due</span>
                  {(() => {
                    const due = savedInvoice ? (savedInvoice.balance_due ?? 0) : total;
                    return (
                      <span className={`font-mono font-extrabold text-base min-w-[80px] text-right ${
                        due > 0 ? 'text-[#EF4444]' : due < 0 ? 'text-[#8B5CF6]' : 'text-[#10B981]'
                      }`}>
                        {due < 0 ? `+${fmt(Math.abs(due))}` : fmt(due)}
                      </span>
                    );
                  })()}
                </div>
              </div>
          </div>
          <BillHistoryCard customerId={customerId} excludeId={savedInvoice?.id || currentDraftId} refreshKey={savedInvoice?.id} beforeOpen={confirmLeave} />
      </div>

      {/* Add Customer Dialog — uses shared AddCustomerDialog so the new
          credentials sub-table + mobile/name/address order are consistent
          everywhere. */}
      <AddCustomerDialog
        open={showAddCustomer}
        onOpenChange={setShowAddCustomer}
        initialName={custForm.name}
        initialPhone={custForm.phone}
        onCreated={(data) => {
          fetchData();
          if (data?.id) setCustomerId(data.id);
        }}
      />

      {/* Add Service Dialog — shared component with Group dropdown / inline
          Group create. Mirrors the EditInvoicePage flow so the popup stays
          consistent across new/edit and Services page entry points. */}
      <AddServiceDialog
        open={showAddService}
        onOpenChange={setShowAddService}
        initialName={addServiceName}
        finAccounts={finAccounts}
        onCreated={handleServiceCreated}
      />

      {/* Receive Due Dialog */}
      <Dialog open={!!showPayDue} onOpenChange={() => setShowPayDue(null)}>
        <DialogContent className="rounded-xl border-[#E4E4E7]">
          <DialogHeader>
            <DialogTitle>Receive Payment</DialogTitle>
            <DialogDescription>Collect due for {showPayDue?.invoice_number} &mdash; Due: {fmt(showPayDue?.balance || 0)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date</Label><Input type="date" value={duePayDate} max={todayIso} onChange={e => setDuePayDate(e.target.value)} className="rounded-lg mt-1" data-testid="due-pay-date" /></div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount</Label><Input type="number" value={duePayAmount} onChange={e => setDuePayAmount(e.target.value)} className="rounded-lg mt-1" data-testid="due-pay-amount" /></div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Payment Mode</Label>
              <Select value={duePayMode} onValueChange={setDuePayMode}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="due-pay-mode"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{finAccounts.filter(a => a.type === 'cash' || a.type === 'bank').map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={handleReceiveDue} className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-xl" data-testid="confirm-due-payment">Receive Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Ledger — uses the shared CustomerLedgerDialog with the
          same `variant="full"` config the Customers Master page uses, so
          this popup mirrors that one exactly (Quick Actions row, Receive
          Due, Refund advance, edit pencils on invoices/receipts). */}
      <CustomerLedgerDialog
        open={showLedgerPopup}
        onOpenChange={setShowLedgerPopup}
        customer={selectedCustomer}
        variant="full"
        onEditInvoice={(invoiceId) => { setShowLedgerPopup(false); navigate(`/invoices/edit/${invoiceId}`); }}
      />

      {/* Saved Login IDs — opens from the Lock icon on the customer summary card. */}
      <CustomerCredentialsDialog
        open={showCredentials}
        onOpenChange={setShowCredentials}
        customerId={selectedCustomer?.id}
        onSaved={() => {
          // Refresh the cached customer list so any name/credential changes
          // propagate into the CustomerCombo without needing a hard reload.
          api.get('/customers').then(r => setCustomers(r.data)).catch(() => {});
        }}
      />

      {/* Edit Customer details — opens from the Pencil icon on the customer summary card. */}
      <EditCustomerDialog
        open={showEditCustomer}
        onOpenChange={setShowEditCustomer}
        customerId={selectedCustomer?.id}
        onSaved={() => {
          // Refresh the customer list — `selectedCustomer` is derived from
          // `customers.find(...)` so the summary card picks up the new
          // name / phone automatically once the list reloads.
          api.get('/customers').then(r => setCustomers(r.data)).catch(() => {});
        }}
      />

      {/* Recurring service period — opens when a recurring service is
          picked, so the operator can confirm/back-date the From date and
          see the auto-filled To date. */}
      <RecurringDatesDialog
        open={!!recurringDialog}
        onOpenChange={(v) => { if (!v) setRecurringDialog(null); }}
        service={recurringDialog?.service}
        initialFromDate={recurringDialog?.initialFrom}
        initialToDate={recurringDialog?.initialTo}
        initialNotes={recurringDialog?.initialNotes}
        onConfirm={({ from, to, notes }) => {
          const idx = recurringDialog?.idx;
          if (idx === undefined) return;
          setItems(prev => prev.map((it, i) => i === idx ? {
            ...it,
            recurring_start_date: from,
            recurring_end_date: to,
            recurring_notes: notes || '',
          } : it));
          setRecurringDialog(null);
        }}
      />

      {/* Fullscreen Quick Finance popup — keeps the invoice draft alive. */}
      <QuickFinanceDialog
        open={!!quickFinanceMode}
        onOpenChange={(v) => { if (!v) setQuickFinanceMode(null); }}
        mode={quickFinanceMode}
        onSaved={() => {
          // Refresh balance-sensitive pills (account list, customer combo,
          // pending bills) so the side-effect of the quick entry is visible.
          api.get('/finance/accounts').then(r => setFinAccounts(r.data || [])).catch(() => {});
          if (selectedCustomer?.id) {
            api.get(`/customers/${selectedCustomer.id}/advance-history`)
              .then(r => setCustomers(prev => prev.map(c => c.id === selectedCustomer.id ? { ...c, advance_balance: r.data?.advance_balance || 0 } : c)))
              .catch(() => {});
          }
        }}
      />

      {/* Reason prompt — required before parking the bill as a draft. */}
      <Dialog open={showDraftReasonPrompt} onOpenChange={setShowDraftReasonPrompt}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-md" data-testid="draft-reason-dialog">
          <DialogHeader>
            <DialogTitle>Save as Draft</DialogTitle>
            <DialogDescription>Why is this bill being parked? The reason helps the next person picking up the task understand the hold.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Reason *</Label>
            <Input
              autoFocus
              value={draftReason}
              onChange={(e) => setDraftReason(e.target.value)}
              placeholder="e.g. Waiting for Aadhaar copy"
              className="rounded-lg mt-1"
              onKeyDown={(e) => { if (e.key === 'Enter' && draftReason.trim()) commitDraft(); }}
              data-testid="draft-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDraftReasonPrompt(false)} className="rounded-xl" data-testid="draft-reason-cancel">Cancel</Button>
            <Button onClick={commitDraft} disabled={!draftReason.trim()} className="bg-[#F59E0B] text-white hover:bg-[#D97706] hover:text-white rounded-xl" data-testid="draft-reason-save">Save Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!negAccounts} onOpenChange={(v) => { if (!v) setNegAccounts(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#DC2626]">Insufficient Balance</AlertDialogTitle>
            <AlertDialogDescription>
              The following account{negAccounts?.length === 1 ? '' : 's'} ha{negAccounts?.length === 1 ? 's' : 've'} insufficient balance for the Govt Fee deduction. Please top up the account or revise the invoice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2">
            {negAccounts?.map((n, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2">
                <p className="font-semibold">{n.name}</p>
                <div className="text-right font-mono text-xs">
                  <p>Available: <span className="text-[#10B981]">{fmt(n.current)}</span></p>
                  <p>Required: <span className="text-[#EF4444]">{fmt(n.deduction)}</span></p>
                </div>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setNegAccounts(null)} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg" data-testid="ok-insufficient-create">OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receive Payment popup — single button next to Due. Records cash,
          adjusts available credit, parks excess. After save we refresh the
          on-screen invoice so the Due field reflects the new state and the
          user can record another partial if needed. */}
      <PaymentReceiptDialog
        open={showPayDialog}
        onOpenChange={setShowPayDialog}
        invoice={savedInvoice}
        receipt={null}
        accounts={finAccounts}
        onSaved={async () => {
          try {
            const r = await api.get(`/invoices/${savedInvoice.id}`);
            setSavedInvoice(r.data);
            // Refresh advance history so the totals card stays accurate.
            if (savedInvoice?.customer_id) {
              const a = await api.get(`/customers/${savedInvoice.customer_id}/advance-history`);
              setAdvHistory({ balance: a.data?.advance_balance || 0, entries: a.data?.entries || [] });
            }
          } catch { /* non-fatal */ }
        }}
      />
    </div>
  );
}
